import { useQueryClient } from '@tanstack/react-query'
import { deliverShare } from './shareLink'
import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { api } from '@/api/client'
import type { AddTrackResponse, Track } from '@/api/types'
import { Button, Input, Sheet } from '@/components/ui'
import type { OwnerShare } from './types'
import { SelectField } from '@/features/settings/SelectField'
import { SettingsDisclosure } from '@/features/settings/SettingsDisclosure'

interface Target {
  kind: 'song' | 'playlist'
  id: number
}
export const useShareSheet = create<{
  target: Target | null
  open: (target: Target) => void
  close: () => void
}>((set) => ({
  target: null,
  open: (target) => set({ target }),
  close: () => set({ target: null }),
}))
export function shareSong(id: number) {
  useShareSheet.getState().open({ kind: 'song', id })
}
export function sharePlaylist(id: number) {
  useShareSheet.getState().open({ kind: 'playlist', id })
}
export async function saveAndShare(track: Pick<Track, 'provider' | 'external_id'>) {
  const { library_item } = await api.post<AddTrackResponse>('/library/tracks', {
    provider: track.provider,
    external_id: track.external_id,
  })
  shareSong(library_item.id)
}
export function ShareControls({
  initial,
  onChange,
  onDismiss,
}: {
  initial: OwnerShare
  onChange?: () => void
  onDismiss?: () => void
}) {
  const queryClient = useQueryClient()
  const [share, setShare] = useState(initial)
  const [days, setDays] = useState('30')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const url = new URL(share.url, window.location.origin).href
  async function act(action: string) {
    setBusy(true)
    setMessage('')
    try {
      setShare(
        await api.patch<OwnerShare>(`/shares/${share.id}`, {
          action,
          days: days === 'never' ? null : Number(days),
        }),
      )
      setMessage(
        action === 'revoke'
          ? 'Sharing stopped. Previously delivered audio cannot be taken back.'
          : action === 'dismiss'
            ? 'Removed from shared links.'
            : action === 'replace'
              ? 'New link ready. The previous link no longer works.'
              : 'This link has been renewed.',
      )
      void queryClient.invalidateQueries({ queryKey: ['shares'] })
      onChange?.()
      if (action === 'dismiss') onDismiss?.()
    } catch (error) {
      setMessage((error as Error).message)
    } finally {
      setBusy(false)
    }
  }
  async function send(native: boolean) {
    try {
      const result = await deliverShare(url, native)
      if (result === 'copied') setMessage('Link copied.')
    } catch (error) {
      if ((error as Error).name !== 'AbortError')
        setMessage('Could not share automatically. Select and copy the link below.')
    }
  }
  return (
    <div className="space-y-4 p-2 text-sm">
      <div>
        <h3 className="font-semibold text-text">{share.title}</h3>
        <p className="mt-1 text-text-muted">
          Anyone with the link can listen, including people it is forwarded to.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-surface-sunken p-3">
        <p className="font-medium text-text">
          {share.revoked
            ? 'Sharing stopped'
            : share.expires_at
              ? `${new Date(share.expires_at).getTime() <= Date.now() ? 'Expired' : 'Expires'} ${new Date(share.expires_at).toLocaleString()}`
              : 'Until revoked'}
        </p>
        <p className="mt-1 text-xs text-text-muted">
          {share.counts.playable} playable · {share.counts.preparing} preparing ·{' '}
          {share.counts.unavailable} unavailable
        </p>
      </div>
      {!share.revoked && (
        <>
          <Input
            label="Unlisted link"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void send(true)}>Share link</Button>
            <Button variant="secondary" onClick={() => void send(false)}>
              Copy link
            </Button>
            <a
              className="inline-flex min-h-11 items-center px-2 text-accent"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Guest preview
            </a>
          </div>
        </>
      )}
      <div className="space-y-3 border-t border-border pt-4">
        <SelectField
          label="Link lifetime"
          value={days}
          onChange={setDays}
          disabled={busy}
          options={[
            { value: '30', label: '30 days' },
            { value: '7', label: '7 days' },
            { value: 'never', label: 'Until revoked' },
          ]}
          hint="Applies when you renew or replace the link."
        />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" disabled={busy || share.revoked} onClick={() => void act('renew')}>
            Renew
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => void act('replace')}>
            Replace link
          </Button>
        </div>
        <p className="text-xs text-text-muted">Replacing a link disables the previous one.</p>
      </div>
      <SettingsDisclosure title="About shared audio">
        <div className="space-y-2 text-sm text-text-muted">
          {share.kind === 'playlist' && (
            <p>This link follows playlist edits. Duplicate the playlist before sharing a fixed mix.</p>
          )}
          <p>
            Only approved, saved audio is playable. Prepare missing songs with the playlist’s download
            action. Provider downloads alone do not grant sharing permission.
          </p>
        </div>
      </SettingsDisclosure>
      {share.revoked ? (
        <div className="space-y-2 border-t border-border pt-4">
          <Button variant="secondary" fullWidth disabled={busy} onClick={() => void act('dismiss')}>
            Dismiss from shared links
          </Button>
          <p className="text-xs text-text-muted">Removes this entry from your list. The link stays stopped.</p>
        </div>
      ) : (
        <div className="border-t border-border pt-4">
          <Button variant="ghost" className="text-danger" disabled={busy} onClick={() => void act('revoke')}>
            Stop sharing
          </Button>
        </div>
      )}
      <p role="status" className="text-text-muted empty:hidden">
        {message}
      </p>
    </div>
  )
}
export function ShareSheet() {
  const queryClient = useQueryClient()
  const { target, close } = useShareSheet()
  const [share, setShare] = useState<OwnerShare | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    let cancelled = false
    setShare(null)
    setError('')
    if (target)
      void api
        .post<OwnerShare>('/shares', target)
        .then((value) => {
          if (!cancelled) {
            setShare(value)
            void queryClient.invalidateQueries({ queryKey: ['shares'] })
          }
        })
        .catch((error: Error) => {
          if (!cancelled) setError(error.message)
        })
    return () => {
      cancelled = true
    }
  }, [target, queryClient])
  return (
    <Sheet
      open={!!target}
      onOpenChange={(open) => {
        if (!open) close()
      }}
      title="Share music"
      description="An unlisted, read-only listening link."
    >
      {share ? (
        <ShareControls key={share.id} initial={share} onDismiss={close} />
      ) : (
        <p role="status" className="p-3">
          {error || 'Preparing your link…'}
        </p>
      )}
    </Sheet>
  )
}
